import { useCreateAnalysis } from '@modules/analysis/hooks/useCreateAnalysis';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { AnalysisUseCase } from 'generated/graphql';
import { useContext } from 'react';
import { FieldNames } from 'v2/core/entities/fields';
import { Scope } from 'v2/core/entities/scope';
import { ExpressionType } from 'v2/core/repository/eql/types';
import { OperationType } from 'v2/core/services/queryBuilder/whereClause';
import { getWhereClauseForFieldName } from 'v2/core/services/queryBuilder/whereClauseGenerator';
import { OpenInCollectionLink } from '../components/FeedbackCard/OpenInCollectionLink';
import { SaveFeedbackContext } from '../components/FeedbackCard/SaveFeedbackContext';

interface CreateCollectionAndAddFeedbackOptions {
  feedbackID: string;
  nameOfCollection: string;
  descriptionOfCollection: string;
}

export const useCreateCollectionAndAddFeedback = ({
  feedbackID,
  descriptionOfCollection,
  nameOfCollection,
}: CreateCollectionAndAddFeedbackOptions) => {
  const { showSuccessNotification } = useNotificationContext();

  const contextData = useContext(SaveFeedbackContext);

  const whereClause = getWhereClauseForFieldName({
    fieldName: FieldNames.ID,
    values: [feedbackID],
    op: OperationType.AND,
  });

  const { createAnalysis, isLoading } = useCreateAnalysis({
    useCase: AnalysisUseCase.Collection,
    name: nameOfCollection,
    description: descriptionOfCollection,
    scope: Scope.PRIVATE,
    eqlProps: {
      whereClause: whereClause,
      expressionType: ExpressionType.Records,
    },
    metadataV2: contextData?.snippetID
      ? {
          collection: {
            savedFeedbackMetadata: [
              {
                feedbackID: feedbackID,
                snippetMetadataV2: [
                  {
                    location: {
                      keywordSummary: {
                        startIdx: 0,
                        endIdx: contextData?.snippetID?.length,
                        msgIdx: contextData.summaryIndex,
                        labelID: contextData.labelID,
                      },
                    },
                  },
                ],
              },
            ],
          },
        }
      : undefined,
  });

  const createCollectionAndAddFeedback = async () => {
    const resp = await createAnalysis({});

    if (typeof resp === 'function' || !resp.data?.createAnalysis?.id) {
      return;
    }

    showSuccessNotification({
      contentComponent: `Feedback record added to "${nameOfCollection}"`,
      props: {
        severity: 'success',
        timeout: 15000,
        isClosable: true,
        action: (
          <OpenInCollectionLink
            viewToken={resp.data?.createAnalysis?.viewToken}
          />
        ),
      },
    });
  };

  return {
    createCollectionAndAddFeedback: createCollectionAndAddFeedback,
    isLoading,
  };
};
